import { gql, useMutation, useSuspenseQuery } from '@apollo/client';
import { Button, ProtectedButton } from 'components/button';
import { Copyable } from 'components/copyable';
import { Dropdown } from 'components/dropdown-v7';
import { Modal } from 'components/modal';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from 'components/table';
import type {
  ClearDosePricingSequenceMutation,
  ClearDosePricingSequenceMutationVariables,
  DosesPageQuery,
  DosesPageQueryVariables,
  SetDosePricingSequenceMutation,
  SetDosePricingSequenceMutationVariables,
} from 'graphql/types';
import { useNotifications } from 'notifications';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Column, Row } from 'react-table';
import { useTable } from 'react-table';

const dosesQuery = gql`
  query DosesPage {
    doses {
      id
      medicineTradeName
      quantity
      unit
      pricingSequence {
        id
      }
    }
    ... @defer {
      sequences(problemTypes: [WEIGHT_LOSS], requiresPrescription: true) {
        id
        internalName
        status
      }
    }
  }
`;

const setDosePricingSequenceMutation = gql`
  mutation SetDosePricingSequence($input: SetDosePricingSequenceInput!) {
    setDosePricingSequence(input: $input) {
      dose {
        id
        pricingSequence {
          id
        }
      }
    }
  }
`;

const clearDosePricingSequenceMutation = gql`
  mutation ClearDosePricingSequence($input: ClearDosePricingSequenceInput!) {
    clearDosePricingSequence(input: $input) {
      dose {
        id
        pricingSequence {
          id
        }
      }
    }
  }
`;

type PricingSequenceForm = {
  sequenceId: string;
};

const PricingSequenceCell = ({
  row,
}: {
  row: Row<
    NonNullable<DosesPageQuery['doses']>[number] & {
      sequences: NonNullable<DosesPageQuery['sequences']>;
    }
  >;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const showNotification = useNotifications();

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    setValue,
  } = useForm<PricingSequenceForm>({
    reValidateMode: 'onChange',
    defaultValues: {
      sequenceId: row.original.pricingSequence?.id ?? '',
    },
  });

  const [setPricingSequence, { loading: settingSequence }] = useMutation<
    SetDosePricingSequenceMutation,
    SetDosePricingSequenceMutationVariables
  >(setDosePricingSequenceMutation, {
    onCompleted: () => {
      showNotification({
        type: 'success',
        message: 'Dose pricing sequence updated successfully',
      });
      setIsModalOpen(false);
    },
    onError: () => {
      showNotification({
        type: 'error',
        message: 'Failed to update dose pricing sequence',
      });
    },
  });

  const [clearPricingSequence, { loading: clearingSequence }] = useMutation<
    ClearDosePricingSequenceMutation,
    ClearDosePricingSequenceMutationVariables
  >(clearDosePricingSequenceMutation, {
    onCompleted: () => {
      showNotification({
        type: 'success',
        message: 'Dose pricing sequence cleared successfully',
      });
      setIsModalOpen(false);
      setValue('sequenceId', '');
    },
    onError: () => {
      showNotification({
        type: 'error',
        message: 'Failed to clear dose pricing sequence',
      });
    },
  });

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1">
        {row.original.pricingSequence?.id ? (
          <Copyable text={row.original.pricingSequence.id}>
            {(copied) => (
              <pre className="text-sm cursor-pointer text-slate-500">
                {copied
                  ? 'Copied'
                  : row.original.pricingSequence?.id?.slice(-6)}
              </pre>
            )}
          </Copyable>
        ) : (
          <span className="text-slate-400">-</span>
        )}
      </div>
      <ProtectedButton
        requiredPermissions={['EDIT_DOSE_PRICING_SEQUENCES']}
        variant="outline"
        size="small"
        onClick={() => setIsModalOpen(true)}
      >
        Edit
      </ProtectedButton>

      <Modal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isAutoOverflow={false}
      >
        <div className="bg-white shadow overflow-visible rounded px-4 py-5 max-w-3xl mx-auto text-slate-800">
          <h3 className="text-lg leading-6 font-medium mb-6">
            Edit Dose ({row.original.id.slice(-6)}) Pricing Sequence
          </h3>
          <form
            onSubmit={handleSubmit((formData) => {
              setPricingSequence({
                variables: {
                  input: {
                    doseId: row.original.id,
                    sequenceId: formData.sequenceId,
                  },
                },
              });
            })}
            className="flex flex-col"
          >
            <div className="mb-4">
              <Dropdown
                {...register('sequenceId', {
                  required: 'Please select a pricing sequence',
                })}
                label="Sequences"
                name="sequenceId"
                control={control}
                errorMessage={errors.sequenceId?.message}
                options={(row.original.sequences ?? [])
                  .filter((s) => s.status === 'AVAILABLE')
                  .map((s) => ({
                    label: s.internalName,
                    value: s.id,
                  }))}
              />
            </div>
            <div className="mt-auto flex justify-between items-center">
              <div>
                {row.original.pricingSequence?.id && (
                  <Button
                    variant="outline"
                    color="danger"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Are you sure you want to clear this dose's pricing sequence?`,
                        )
                      ) {
                        clearPricingSequence({
                          variables: {
                            input: {
                              doseId: row.original.id,
                            },
                          },
                        });
                      }
                    }}
                    loading={clearingSequence}
                    disabled={settingSequence}
                  >
                    Clear Pricing Sequence
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setValue(
                      'sequenceId',
                      row.original.pricingSequence?.id ?? '',
                    );
                    setIsModalOpen(false);
                  }}
                  type="button"
                  disabled={settingSequence || clearingSequence}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={settingSequence}
                  disabled={clearingSequence}
                >
                  Save
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

const columns: Column<
  NonNullable<DosesPageQuery['doses']>[number] & {
    sequences: NonNullable<DosesPageQuery['sequences']>;
  }
>[] = [
  {
    Header: 'ID',
    accessor: 'id',
    Cell: (c) => (
      <Copyable text={c.value}>
        {(copied) => (
          <pre className="text-sm cursor-pointer text-slate-500">
            {copied ? 'Copied' : c.value.slice(-6)}
          </pre>
        )}
      </Copyable>
    ),
    className: 'w-1/5',
    disableSortBy: false,
  },
  {
    Header: 'Medication',
    accessor: 'medicineTradeName',
    Cell: (c) => <div>{c.value}</div>,
    className: 'w-1/5',
    disableSortBy: false,
  },
  {
    Header: 'Quantity',
    accessor: 'quantity',
    Cell: (c) => <div>{c.value}</div>,
    className: 'w-1/5',
    disableSortBy: false,
  },
  {
    Header: 'Unit',
    accessor: 'unit',
    Cell: (c) => <div>{c.value}</div>,
    className: 'w-1/5',
    disableSortBy: false,
  },
  {
    Header: 'Pricing Sequence',
    accessor: 'pricingSequence',
    Cell: (c) => <PricingSequenceCell row={c.row} />,
    className: 'w-1/5',
    disableSortBy: false,
  },
];

const DosesPage = (): React.ReactElement => {
  const { data, error } = useSuspenseQuery<
    DosesPageQuery,
    DosesPageQueryVariables
  >(dosesQuery);

  const tableInstance = useTable({
    columns,
    data:
      data?.doses?.map((d) => ({
        ...d,
        sequences: data?.sequences ?? [],
      })) ?? [],
  });

  return (
    <div>
      <Table tableInstance={tableInstance}>
        <TableHead />
        <TableBody>
          <React.Fragment>
            {tableInstance.rows.map((row) => {
              tableInstance.prepareRow(row);
              return (
                <TableRow row={row} key={row.id}>
                  {row.cells.map((cell) => (
                    <TableCell
                      key={`${cell.row.original.id}-${cell.column.id}`}
                      cell={cell}
                    />
                  ))}
                </TableRow>
              );
            })}
          </React.Fragment>
        </TableBody>
      </Table>
      {error && (
        <div>
          Error - Unable to load doses, please contact #help-technology.
        </div>
      )}
      {!data?.doses?.length && (
        <div className="text-center font-medium pt-8">No doses found</div>
      )}
    </div>
  );
};

export default DosesPage;
